import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, Clock, DollarSign, Star, AlertCircle, CheckCircle, 
  Briefcase, MapPin, Users
} from 'lucide-react';
import { useContractorAuth } from '../../contexts/ContractorAuthContext';

export const ContractorDashboard = () => {
  const { contractor, api } = useContractorAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    currentJob: null,
    upcomingJobs: [],
    schedule: [],
    performance: null,
    notifications: []
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // In a real implementation, this would fetch from dedicated contractor endpoints
      // For now, we'll show placeholder data
      setDashboardData({
        currentJob: null,
        upcomingJobs: [],
        schedule: [],
        performance: {
          overall_grade: 4.5,
          jobs_completed: 12,
          on_time_rate: 95
        },
        notifications: []
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-[#C6A87C]/20 to-[#C6A87C]/5 border border-[#C6A87C]/30 p-6"
      >
        <h1 className="text-2xl font-serif text-white mb-1">
          Welcome back, {contractor?.name?.split(' ')[0]}!
        </h1>
        <p className="text-gray-400 text-sm">
          Contractor ID: <span className="font-mono text-[#C6A87C]">{contractor?.contractor_id}</span>
        </p>
      </motion.div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/5 border border-white/10 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{dashboardData.performance?.jobs_completed || 0}</p>
              <p className="text-xs text-gray-400">Jobs Completed</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/5 border border-white/10 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#C6A87C]/20 flex items-center justify-center">
              <Star className="w-5 h-5 text-[#C6A87C]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{dashboardData.performance?.overall_grade || '-'}</p>
              <p className="text-xs text-gray-400">Performance Rating</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/5 border border-white/10 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{dashboardData.performance?.on_time_rate || 0}%</p>
              <p className="text-xs text-gray-400">On-Time Rate</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white/5 border border-white/10 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{dashboardData.upcomingJobs?.length || 0}</p>
              <p className="text-xs text-gray-400">Upcoming Jobs</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Current Assignment */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="bg-white/5 border border-white/10 p-6"
      >
        <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-[#C6A87C]" />
          Current Assignment
        </h2>
        
        {dashboardData.currentJob ? (
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <h3 className="text-white font-medium">{dashboardData.currentJob.title}</h3>
                <p className="text-gray-400 text-sm flex items-center gap-1 mt-1">
                  <MapPin className="w-4 h-4" />
                  {dashboardData.currentJob.location}
                </p>
              </div>
              <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 border border-green-500/30">
                Active
              </span>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <Briefcase className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No active assignment</p>
            <p className="text-gray-500 text-sm mt-1">Check your schedule for upcoming work</p>
          </div>
        )}
      </motion.div>

      {/* Today's Schedule */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="bg-white/5 border border-white/10 p-6"
      >
        <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#C6A87C]" />
          Today's Schedule
        </h2>
        
        {dashboardData.schedule.length > 0 ? (
          <div className="space-y-3">
            {dashboardData.schedule.map((item, idx) => (
              <div key={idx} className="flex items-center gap-4 p-3 bg-white/5 border border-white/10">
                <div className="text-[#C6A87C] font-mono text-sm">{item.time}</div>
                <div className="flex-1">
                  <p className="text-white text-sm">{item.title}</p>
                  <p className="text-gray-500 text-xs">{item.location}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No scheduled work for today</p>
          </div>
        )}
      </motion.div>

      {/* Important Notices */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="bg-blue-500/10 border border-blue-500/20 p-4"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-300 text-sm font-medium">Welcome to your Contractor Portal</p>
            <p className="text-blue-400/70 text-xs mt-1">
              Here you can view your schedule, check your pay, track your performance, and access training materials.
              If you have any questions, contact your supervisor.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ContractorDashboard;
